/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * CopyableInlineLinkText
 *
 * Renders inline text with optional links. Links open in a new tab and copy as
 * "LinkText [url]" when the surrounding content is copied.
 *
 * @file CopyableInlineLinkText.jsx
 * @since 2.3.8
 * @author Anuja Gaikwad
 */

import React from 'react';
import PropTypes from 'prop-types';
import { getLinkCopyText } from './copyableInlineLinkTextUtils';

const CopyableInlineLinkText = ({ segments, linkClassName }) => (
  segments.map((segment, index) => {
    if (segment.isLink) {
      const copyText = segment.copyText || getLinkCopyText(segment.text, segment.href);

      return (
        <a
          key={segment.id || `copyable-link-${index}`}
          href={segment.href}
          target="_blank"
          rel="noopener noreferrer"
          className={linkClassName}
          data-copy-text={copyText}
        >
          {segment.text}
        </a>
      );
    }

    return (
      <span key={segment.id || `copyable-text-${index}`}>
        {segment.text}
      </span>
    );
  })
);

CopyableInlineLinkText.propTypes = {
  segments: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    text: PropTypes.string,
    isLink: PropTypes.bool,
    href: PropTypes.string,
    copyText: PropTypes.string
  })).isRequired,
  linkClassName: PropTypes.string
};

CopyableInlineLinkText.defaultProps = {
  linkClassName: ''
};

export default CopyableInlineLinkText;
